import List from 'antd/es/list';
import { ComponentProps } from 'react';
import clsx from 'clsx';

import './OpList.scss';

interface OpListItemMetaProps extends ComponentProps<typeof List.Item.Meta> {
  testId?: string;
  gtm?: string;
}

export const OpListItemMeta = ({
  className,
  testId,
  gtm,
  ...listItemMetaProps
}: OpListItemMetaProps) => {
  return (
    <List.Item.Meta
      className={clsx('op-list-item-meta', className)}
      data-testid={testId}
      data-gtm={gtm}
      {...listItemMetaProps}
    />
  );
};

interface OpListItemProps extends ComponentProps<typeof List.Item> {
  testId?: string;
  gtm?: string;
}

export const OpListItem = ({
  className,
  testId,
  gtm,
  ...listItemProps
}: OpListItemProps) => {
  return (
    <List.Item
      className={clsx('op-list-item', className)}
      data-testid={testId}
      data-gtm={gtm}
      {...listItemProps}
    />
  );
};

interface OpListProps extends ComponentProps<typeof List> {
  testId?: string;
  gtm?: string;
  /** Makes the list header sticky to the top when scrolling */
  stickyHeader?: boolean;
}

/**
 * OpList is a wrapper around Ant Design's List component.
 * It displays a list of items with consistent formatting and optional metadata.
 *
 * Use this component to display structured lists of data such as messages, notifications,
 * articles, or any collection of similar items. Supports pagination, loading states,
 * and customizable item rendering. Includes OpList.Item and OpList.Item.Meta sub-components.
 *
 * @example
 * <OpList
 *   dataSource={items}
 *   header={<div>Sticky Header</div>}
 *   stickyHeader
 *   renderItem={(item) => (
 *     <OpList.Item>
 *       <OpList.Item.Meta title={item.title} description={item.description} />
 *     </OpList.Item>
 *   )}
 * />
 *
 */
export const OpList = ({
  className,
  testId,
  gtm,
  stickyHeader = false,
  ...listProps
}: OpListProps) => {
  return (
    <List
      className={clsx(
        'op-list',
        {
          'op-list--sticky-header': stickyHeader,
        },
        className,
      )}
      data-testid={testId}
      data-gtm={gtm}
      {...listProps}
    />
  );
};

OpListItem.Meta = OpListItemMeta;
OpList.Item = OpListItem;
